import { resolveGroupColor } from "./resolveGroupColor";
import type { UpdatePlanElement } from "./types";

export const updatePlanElement: UpdatePlanElement = ({
  plan,
  fromGroupLabel,
  elementId,
  changes,
}) => {
  const fromElements = plan.groupedElements.byGroupId[fromGroupLabel] ?? [];
  const element = fromElements.find((e) => e.id === elementId);

  if (!element) {
    return plan;
  }

  const toGroupLabel = changes.toGroupLabel ?? fromGroupLabel;
  const groupChanged = toGroupLabel !== fromGroupLabel;
  const updatedElement = {
    ...element,
    ...(changes.bbox && { bbox: changes.bbox }),
    ...(changes.multiplier !== undefined && {
      multiplier: changes.multiplier,
    }),
    ...(changes.note !== undefined && { note: changes.note }),
    group_id: toGroupLabel,
    ...(groupChanged && {
      rgb: resolveGroupColor({
        groupName: toGroupLabel,
        byGroupId: plan.groupedElements.byGroupId,
      }),
    }),
  };

  // Stays in place within its group; only a move to another group appends it there.
  const newFromElements = groupChanged
    ? fromElements.filter((e) => e.id !== elementId)
    : fromElements.map((e) => (e.id === elementId ? updatedElement : e));
  const byGroupId = groupChanged
    ? {
        ...plan.groupedElements.byGroupId,
        [fromGroupLabel]: newFromElements,
        [toGroupLabel]: [
          ...(plan.groupedElements.byGroupId[toGroupLabel] ?? []),
          updatedElement,
        ],
      }
    : { ...plan.groupedElements.byGroupId, [fromGroupLabel]: newFromElements };

  let groupIds = plan.groupedElements.groupIds;
  let groupMeta = plan.groupMeta;

  if (groupChanged) {
    if (newFromElements.length === 0) {
      groupIds = groupIds.filter((id) => id !== fromGroupLabel);
      const { [fromGroupLabel]: _removedMeta, ...restGroupMeta } = groupMeta;
      groupMeta = restGroupMeta;
    }
    if (!groupIds.includes(toGroupLabel)) {
      groupIds = [...groupIds, toGroupLabel];
    }
    if (!groupMeta[toGroupLabel] && changes.newGroupElementTypeId) {
      groupMeta = {
        ...groupMeta,
        [toGroupLabel]: {
          element_type_id: changes.newGroupElementTypeId,
          // Matches backend: element types created via the manual update endpoint are CONFIDENT
          nameStatus: "confident",
        },
      };
    }
  }

  const multiplierDelta = updatedElement.multiplier - element.multiplier;
  const all =
    multiplierDelta === 0
      ? plan.groupedElements.all
      : {
          ...plan.groupedElements.all,
          count: Math.max(0, plan.groupedElements.all.count + multiplierDelta),
        };

  return {
    ...plan,
    groupedElements: {
      ...plan.groupedElements,
      byGroupId,
      groupIds,
      all,
    },
    groupMeta,
  };
};
